import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Award, BookOpen, GraduationCap } from 'lucide-react';
import ExamManagement from './ExamManagement';
import CourseInstanceManagement from './CourseInstanceManagement';
import SensorManagement from './SensorManagement';

type TabType = 'exams' | 'courses' | 'sensors';

export default function CoursesAndExams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(tabParam || 'exams');

  // Sync URL with active tab
  useEffect(() => {
    if (tabParam && ['exams', 'courses', 'sensors'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const tabs = [
    {
      id: 'exams' as TabType,
      label: 'Prøvesystem',
      icon: <Award size={18} />,
      description: 'Administrer prøvesett og eksamensspørsmål'
    },
    {
      id: 'courses' as TabType,
      label: 'Kurs',
      icon: <BookOpen size={18} />,
      description: 'Administrer kursgjennomføringer og kandidater'
    },
    {
      id: 'sensors' as TabType,
      label: 'Sensorer',
      icon: <GraduationCap size={18} />,
      description: 'Administrer sensorer for sensur av prøver'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Kurs og prøver</h1>
        <p className="text-gray-600 mt-2">
          Administrer kursgjennomføringer, prøvesett og sensorer
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`
                group inline-flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${activeTab === tab.id
                  ? 'border-kf-primary text-kf-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
              title={tab.description}
            >
              <span className={`${activeTab === tab.id ? 'text-kf-primary' : 'text-gray-400 group-hover:text-gray-500'}`}>
                {tab.icon}
              </span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {activeTab === 'exams' && <ExamManagement />}
        {activeTab === 'courses' && <CourseInstanceManagement />}
        {activeTab === 'sensors' && <SensorManagement />}
      </div>
    </div>
  );
}
